import { ApiReference } from "../shared/interfaces";
import { resolve, resolveBatch, RESOLVE_BATCH_LIMIT, ResolvedIds } from "../utils/resolver";

/**
 * Resolver caches (`null` = resolution failed; degrade gracefully). Two
 * axes because a reference's `fileId` is usually an EXCERPT file (which the
 * resolver misses), while its `sectionUuid` resolves — via the resolver's
 * fragment-node param — to the real sheet + section group, which is what
 * lets a drawing citation open as a SHEET rather than a raw PDF. Keyed per
 * page-load only — an org/environment switch reloads the page and clears
 * them, so entries can never leak across tenants.
 */
export const resolvedFiles = new Map<string, ResolvedIds | null>();
export const resolvedSections = new Map<string, ResolvedIds | null>();
const resolutionsInFlight = new Map<string, Promise<void>>();
const MAX_CONCURRENT_REFERENCE_RESOLUTIONS = 6;
let activeReferenceResolutions = 0;
const referenceResolutionWaiters: Array<() => void> = [];

async function withReferenceResolutionSlot<T>(work: () => Promise<T>): Promise<T> {
    if (activeReferenceResolutions >= MAX_CONCURRENT_REFERENCE_RESOLUTIONS) {
        await new Promise<void>((resolveSlot) => referenceResolutionWaiters.push(resolveSlot));
    }
    activeReferenceResolutions += 1;
    try {
        return await work();
    } finally {
        activeReferenceResolutions -= 1;
        referenceResolutionWaiters.shift()?.();
    }
}

interface ResolutionTarget {
    kind: "section" | "file";
    id: string;
}

function cacheFor(target: ResolutionTarget): Map<string, ResolvedIds | null> {
    return target.kind === "section" ? resolvedSections : resolvedFiles;
}

function inFlightKey(target: ResolutionTarget): string {
    return `${target.kind}:${target.id}`;
}

/**
 * One chunk of targets: a single batch round-trip, holding one of the shared
 * concurrency slots so many chunks (or the offline-prefetch workers) cannot
 * fan out unbounded simultaneous batch POSTs. When the batch route is
 * unavailable (older backend) or fails, degrades to per-id resolves — the
 * chunk's slot is released before the fallback acquires its own, so the
 * fallback cannot deadlock on the limiter.
 */
async function resolveChunk(targets: ResolutionTarget[]): Promise<void> {
    const sectionIds = targets.filter((t) => t.kind === "section").map((t) => t.id);
    const fileIds = targets.filter((t) => t.kind === "file").map((t) => t.id);
    try {
        const result = await withReferenceResolutionSlot(() => resolveBatch(sectionIds, fileIds));
        for (const id of sectionIds) resolvedSections.set(id, result.fragmentNodes[id] ?? null);
        for (const id of fileIds) resolvedFiles.set(id, result.files[id] ?? null);
    } catch {
        await Promise.all(
            targets.map((target) =>
                withReferenceResolutionSlot(() =>
                    resolve(
                        target.kind === "section"
                            ? { fragmentNodeId: target.id }
                            : { fileId: target.id },
                    ),
                )
                    .catch(() => null)
                    .then((resolved) => {
                        cacheFor(target).set(target.id, resolved);
                    }),
            ),
        );
    }
}

/**
 * Resolves every not-yet-resolved reference target (section uuids
 * first-class, file ids as fallback); no-op when all are cached. New targets
 * go out as batched round-trips chunked at RESOLVE_BATCH_LIMIT; targets a
 * concurrent caller already has in flight are awaited, never re-requested.
 */
export async function ensureReferencesResolved(references: ApiReference[]): Promise<void> {
    const tasks: Promise<void>[] = [];
    const pending: ResolutionTarget[] = [];
    const seen = new Set<string>();
    for (const reference of references) {
        const targets: ResolutionTarget[] = [{ kind: "file", id: reference.fileId }];
        if (reference.sectionUuid) targets.push({ kind: "section", id: reference.sectionUuid });
        for (const target of targets) {
            const key = inFlightKey(target);
            if (cacheFor(target).has(target.id) || seen.has(key)) continue;
            seen.add(key);
            const inFlight = resolutionsInFlight.get(key);
            if (inFlight) {
                tasks.push(inFlight);
            } else {
                pending.push(target);
            }
        }
    }
    for (let i = 0; i < pending.length; i += RESOLVE_BATCH_LIMIT) {
        const chunk = pending.slice(i, i + RESOLVE_BATCH_LIMIT);
        const task = resolveChunk(chunk).finally(() => {
            for (const target of chunk) resolutionsInFlight.delete(inFlightKey(target));
        });
        for (const target of chunk) resolutionsInFlight.set(inFlightKey(target), task);
        tasks.push(task);
    }
    if (tasks.length > 0) await Promise.all(tasks);
}
